<template>
  <div class="supplier-detail-view">
    <div class="page-header">
      <div class="header-left">
        <button @click="$router.push({ name: 'suppliers' })" class="back-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div class="header-info">
          <h1 class="page-heading">Fiche fournisseur</h1>
          <p class="page-subtitle">Détails et historique des commandes</p>
        </div>
      </div>
      <div v-if="supplier.id" class="header-actions">
        <button @click="createInvoice" class="action-btn primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="12" y1="18" x2="12" y2="12"></line>
            <line x1="9" y1="15" x2="15" y2="15"></line>
          </svg>
          Créer une facture
        </button>
        <button @click="editSupplier" class="action-btn secondary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
          </svg>
          Modifier
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Chargement des informations...</p>
    </div>

    <div v-else-if="!supplier.id" class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <h3>Fournisseur introuvable</h3>
      <p>Le fournisseur que vous recherchez n'existe pas ou a été supprimé</p>
      <button @click="$router.push({ name: 'suppliers' })" class="back-to-list-btn">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Retour à la liste
      </button>
    </div>

    <div v-else class="content-layout">
      <div class="sidebar">
        <div class="card profile-card">
          <div class="profile-header">
            <div class="profile-avatar">
              {{ getInitials(supplier.name) }}
            </div>
            <div class="profile-badge" :class="supplier.active ? 'active' : 'inactive'">
              <span class="badge-dot"></span>
              {{ supplier.active ? 'Actif' : 'Inactif' }}
            </div>
          </div>

          <div class="profile-content">
            <h2 class="supplier-name">{{ supplier.name }}</h2>
            <div class="supplier-meta">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>Créé le {{ formatDate(supplier.created_at) }}</span>
            </div>
          </div>

          <div class="contact-section">
            <div class="contact-header">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <span>Informations de contact</span>
            </div>

            <div v-if="supplier.contact_person" class="contact-item">
              <div class="contact-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <div class="contact-details">
                <span class="contact-label">Personne contact</span>
                <span class="contact-value">{{ supplier.contact_person }}</span>
              </div>
            </div>

            <div v-if="supplier.phone" class="contact-item">
              <div class="contact-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z">
                  </path>
                </svg>
              </div>
              <div class="contact-details">
                <span class="contact-label">Téléphone</span>
                <a :href="'tel:' + supplier.phone" class="contact-value link">{{ supplier.phone }}</a>
              </div>
            </div>

            <div v-if="supplier.email" class="contact-item">
              <div class="contact-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <div class="contact-details">
                <span class="contact-label">Email</span>
                <a :href="'mailto:' + supplier.email" class="contact-value link">{{ supplier.email }}</a>
              </div>
            </div>

            <div v-if="supplier.full_address" class="contact-item">
              <div class="contact-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <div class="contact-details">
                <span class="contact-label">Adresse</span>
                <span class="contact-value">{{ supplier.full_address }}</span>
              </div>
            </div>

            <div v-if="!supplier.phone && !supplier.email && !supplier.full_address && !supplier.contact_person"
              class="no-contact">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>Aucune information de contact</p>
            </div>
          </div>

          <div v-if="supplier.notes" class="notes-section">
            <div class="notes-header">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span>Notes</span>
            </div>
            <div class="notes-content">{{ supplier.notes }}</div>
          </div>
        </div>

        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon orders">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ supplierStats.ordersCount }}</span>
              <span class="stat-label">Commandes</span>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon revenue">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ formatCurrency(supplierStats.totalPurchased) }}</span>
              <span class="stat-label">Total acheté</span>
            </div>
          </div>

          <div class="stat-card" :class="{ 'stat-warning': supplierStats.unreceivedCount > 0 }">
            <div class="stat-icon" :class="supplierStats.unreceivedCount > 0 ? 'pending' : 'neutral'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ supplierStats.unreceivedCount }}</span>
              <span class="stat-label">En attente</span>
            </div>
          </div>
        </div>
      </div>

      <div class="main-content">
        <div class="card orders-card">
          <div class="card-header">
            <div class="header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
            </div>
            <div class="header-text">
              <h3>Historique des commandes</h3>
              <p>{{ supplierStats.ordersCount }} commande{{ supplierStats.ordersCount > 1 ? 's' : '' }}</p>
            </div>
          </div>

          <div v-if="ordersLoading" class="orders-loading">
            <div class="spinner"></div>
            <p>Chargement des commandes...</p>
          </div>

          <div v-else-if="orders.length === 0" class="orders-empty">
            <div class="empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
            </div>
            <h4>Aucune commande</h4>
            <p>Ce fournisseur n'a pas encore de commandes enregistrées</p>
          </div>

          <div v-else class="orders-list">
            <div v-for="order in orders" :key="order.id" class="order-item" @click="viewOrder(order)">
              <div class="order-main">
                <div class="order-left">
                  <div class="order-reference">
                    <div class="reference-badge">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div class="reference-info">
                      <span class="reference-number">{{ order.reference }}</span>
                      <span class="reference-date">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        {{ formatDate(order.order_date || order.created_at) }}
                      </span>
                    </div>
                  </div>

                  <div class="order-details">
                    <div class="order-detail-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                        <line x1="7" y1="7" x2="7.01" y2="7"></line>
                      </svg>
                      <span>{{ order.items_count }} article{{ order.items_count > 1 ? 's' : '' }}</span>
                    </div>
                    <div class="order-detail-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <span>{{ order.user?.name || '-' }}</span>
                    </div>
                    <div class="order-detail-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      <span :class="order.has_invoice ? 'invoice-badge invoiced' : 'invoice-badge not-invoiced'">
                        {{ order.has_invoice ? 'Facturée' : 'Non facturée' }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="order-right">
                  <div class="order-status">
                    <span class="status-badge" :class="getOrderStatusClass(order.status)">
                      <span class="status-dot"></span>
                      {{ getOrderStatusLabel(order.status) }}
                    </span>
                  </div>
                  <div class="order-amount">{{ formatCurrency(order.total_amount || 0) }}</div>
                </div>
              </div>

              <button class="order-action-btn" @click.stop="viewOrder(order)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import supplierService from '@/services/supplierService'
import purchaseOrderService from '@/services/purchaseOrderService'

export default defineComponent({
  name: 'SupplierDetailView',
  setup() {
    const route = useRoute()
    const router = useRouter()

    const supplier = ref({})
    const loading = ref(true)
    const orders = ref([])
    const ordersLoading = ref(false)

    const supplierStats = ref({
      ordersCount: 0,
      totalPurchased: 0,
      unreceivedCount: 0
    })

    const loadSupplier = async () => {
      const id = route.params.id
      if (!id) {
        router.push({ name: 'suppliers' })
        return
      }
      try {
        loading.value = true
        const res = await supplierService.getSupplier(id)
        supplier.value = res.data.data || res.data
        await loadSupplierOrders()
      } catch (e) {
        console.error('Erreur chargement fournisseur:', e)
        supplier.value = {}
      } finally {
        loading.value = false
      }
    }

    const loadSupplierOrders = async () => {
      try {
        ordersLoading.value = true
        const res = await purchaseOrderService.getPurchaseOrders({
          supplier_id: supplier.value.id,
          per_page: 10,
          page: 1,
          sort_by: 'created_at',
          sort_direction: 'desc'
        })
        const data = res.data
        const list = Array.isArray(data) ? data : (data.data || data.items || [])
        orders.value = list

        supplierStats.value.ordersCount = Number(Array.isArray(data) ? list.length : (data.total || list.length) || 0)
        supplierStats.value.totalPurchased = list.reduce((acc, o) => acc + Number(o.total_amount || o.total_incl_tax || o.total || 0), 0)
        supplierStats.value.unreceivedCount = list.filter(o => (o.status === 'pending' || o.status === 'ordered' || o.status === 'inactive')).length
      } catch (e) {
        console.error('Erreur chargement commandes fournisseur:', e)
        orders.value = []
      } finally {
        ordersLoading.value = false
      }
    }

    const viewOrder = (order) => {
      router.push({ name: 'purchase-order-details', params: { id: order.id } })
    }

    const editSupplier = () => {
      console.log('TODO: Naviguer vers édition fournisseur', supplier.value.id)
    }

    const createInvoice = () => {
      router.push({ name: 'supplier-invoices-new', params: { supplierId: supplier.value.id } })
    }

    const getOrderStatusLabel = (status) => {
      const map = {
        active: 'Active',
        inactive: 'Inactive',
        pending: 'En attente',
        ordered: 'Commandée',
        received: 'Réceptionnée',
        cancelled: 'Annulée'
      }
      return map[status] || status
    }

    const getOrderStatusClass = (status) => {
      const map = {
        active: 'status-active',
        inactive: 'status-inactive',
        pending: 'status-pending',
        ordered: 'status-ordered',
        received: 'status-received',
        cancelled: 'status-cancelled'
      }
      return map[status] || 'status-inactive'
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0) + ' FCFA'
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    onMounted(() => {
      loadSupplier()
    })

    return {
      supplier,
      loading,
      orders,
      ordersLoading,
      supplierStats,
      viewOrder,
      editSupplier,
      createInvoice,
      getOrderStatusLabel,
      getOrderStatusClass,
      formatCurrency,
      formatDate,
      getInitials
    }
  }
})
</script>

<style scoped>
.supplier-detail-view {
  padding: 0 0 24px 0;
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #0A5C6B;
}

.back-btn svg {
  width: 20px;
  height: 20px;
  stroke: #475569;
  stroke-width: 2;
}

.header-info {
  flex: 1;
}

.page-heading {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.action-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.action-btn.primary {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(10, 92, 107, 0.2);
}

.action-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.action-btn.secondary {
  background: white;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}

.action-btn.secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 20px;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(10, 92, 107, 0.1);
  border-top-color: #0A5C6B;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-container p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 16px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon svg {
  width: 40px;
  height: 40px;
  stroke: #c62828;
  stroke-width: 2;
}

.empty-state h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.back-to-list-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.back-to-list-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(10, 92, 107, 0.3);
}

.back-to-list-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.content-layout {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.profile-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
}

.profile-header {
  position: relative;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: white;
  color: #0A5C6B;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 800;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.profile-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
}

.profile-badge.active {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.profile-badge.inactive {
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  color: #616161;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.profile-badge.active .badge-dot {
  background: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
}

.profile-badge.inactive .badge-dot {
  background: #616161;
  box-shadow: 0 0 0 3px rgba(97, 97, 97, 0.2);
}

.profile-content {
  padding: 24px;
  text-align: center;
  border-bottom: 2px solid #e2e8f0;
}

.supplier-name {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 12px 0;
  letter-spacing: -0.5px;
}

.supplier-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.supplier-meta svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}

.contact-section,
.notes-section {
  padding: 24px;
  border-bottom: 2px solid #e2e8f0;
}

.contact-section:last-child,
.notes-section:last-child {
  border-bottom: none;
}

.contact-header,
.notes-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.contact-header svg,
.notes-header svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
  stroke: #94a3b8;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.2s;
}

.contact-item:last-child {
  margin-bottom: 0;
}

.contact-item:hover {
  background: #f8fafc;
}

.contact-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.contact-icon svg {
  width: 18px;
  height: 18px;
  stroke: #0284c7;
  stroke-width: 2;
}

.contact-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-label {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 600;
}

.contact-value {
  font-size: 14px;
  color: #0f172a;
  font-weight: 600;
  word-break: break-word;
}

.contact-value.link {
  color: #0A5C6B;
  text-decoration: none;
  transition: color 0.2s;
}

.contact-value.link:hover {
  color: #094854;
  text-decoration: underline;
}

.no-contact {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  gap: 12px;
}

.no-contact svg {
  width: 32px;
  height: 32px;
  stroke: #cbd5e1;
  stroke-width: 2;
}

.no-contact p {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}

.notes-content {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  background: white;
  padding: 14px;
  border-radius: 8px;
}

.stats-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stat-card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
}

.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.stat-card.stat-warning {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-color: #fde68a;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon svg {
  width: 24px;
  height: 24px;
  stroke-width: 2;
}

.stat-icon.orders {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
}

.stat-icon.orders svg {
  stroke: #1976d2;
}

.stat-icon.revenue {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.stat-icon.revenue svg {
  stroke: #2e7d32;
}

.stat-icon.pending {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
}

.stat-icon.pending svg {
  stroke: #e65100;
}

.stat-icon.neutral {
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
}

.stat-icon.neutral svg {
  stroke: #616161;
}

.stat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.5px;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.main-content {
  display: flex;
  flex-direction: column;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 2px solid #e2e8f0;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-icon svg {
  width: 24px;
  height: 24px;
  stroke: #7b1fa2;
  stroke-width: 2;
}

.header-text {
  flex: 1;
}

.header-text h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.header-text p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.orders-loading,
.orders-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
}

.orders-empty .empty-icon {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
}

.orders-empty .empty-icon svg {
  stroke: #94a3b8;
}

.orders-empty h4 {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.orders-empty p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.orders-list {
  display: flex;
  flex-direction: column;
}

.order-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: all 0.2s;
}

.order-item:hover {
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
}

.order-item:last-child {
  border-bottom: none;
}

.order-main {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.order-left {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-reference {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reference-badge {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.reference-badge svg {
  width: 20px;
  height: 20px;
  stroke: #0284c7;
  stroke-width: 2;
}

.reference-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reference-number {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.reference-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.reference-date svg {
  width: 14px;
  height: 14px;
  stroke-width: 2;
}

.order-details {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.order-detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
}

.order-detail-item svg {
  width: 14px;
  height: 14px;
  stroke-width: 2;
}

.invoice-badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
}

.invoice-badge.invoiced {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.invoice-badge.not-invoiced {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  color: #e65100;
}

.order-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.order-status {
  display: flex;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-badge.status-active {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.status-badge.status-active .status-dot {
  background: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
}

.status-badge.status-inactive {
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  color: #616161;
}

.status-badge.status-inactive .status-dot {
  background: #616161;
  box-shadow: 0 0 0 3px rgba(97, 97, 97, 0.2);
}

.status-badge.status-cancelled {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  color: #c62828;
}

.status-badge.status-cancelled .status-dot {
  background: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.2);
}

.order-amount {
  font-size: 18px;
  font-weight: 800;
  color: #0A5C6B;
}

.order-action-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.order-action-btn:hover {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  border-color: #0A5C6B;
}

.order-action-btn svg {
  width: 18px;
  height: 18px;
  stroke: #475569;
  stroke-width: 2;
  transition: stroke 0.2s;
}

.order-action-btn:hover svg {
  stroke: white;
}

@media (max-width: 1280px) {
  .content-layout {
    grid-template-columns: 340px 1fr;
  }
}

@media (max-width: 1024px) {
  .content-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .supplier-detail-view {
    padding: 0 0 16px 0;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    flex-direction: column;
    align-items: stretch;
  }

  .back-btn {
    align-self: flex-start;
  }

  .header-actions {
    width: 100%;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .order-main {
    flex-direction: column;
    align-items: stretch;
  }

  .order-right {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}
</style>